<template>
  <div
    id="options-sidebar"
    :class="[
      'site-tabs options-sidebar',
      `status-${auctionStatus.toLowerCase()}`,
      { 'auction-not-started': auctionStatus === AuctionStatuses.ACTIVE }
    ]">
    <div class="main-panel box-shadow">
      <div class="controller-content tabs-content">
        <div
          v-for="tab in tabs"
          class="tab-content"
          :class="[tab.class || '', { active: activeTab === tab.key }]"
          :key="tab.key">
          <component
            v-show="activeTab === tab.key"
            :is-visible="activeTab === tab.key"
            :is="tab.component"
            @changeTab="activeTab = $event"
            :id="`${tab.key}RefComponent`"></component>
        </div>
      </div>
      <live-bid-block v-if="!auctionStore.user?.is_admin && auctionStatus !== AuctionStatuses.ENDED" @changeTab="activeTab = $event"></live-bid-block>
    </div>
    <div class="controller-tabs">
      <div class="controller-tabs-icons">
        <div
          v-for="tab in tabs"
          class="tab-title"
          :class="[tab.class || '', { active: activeTab === tab.key }]"
          :key="tab.key"
          :id="`${tab.key}Ref`"
          @click="changeActiveTab(tab)">
          <base-icon :icon-name="tab.iconName" :view-box="tab.iconViewBox || defaultViewBox">
            <component :is="tab.icon" />
          </base-icon>
        </div>
      </div>
    </div>
  </div>
  <auth-popup :message="trans('live.modals.auth_action_tabs.message')" ref="authPopupRef" />
</template>

<script setup lang="ts">
  import { computed, inject, markRaw, ref, watch } from "vue";
  import { trans } from "@/utils/trans";
  import {
    Chat,
    Statistic,
    Currency,
    History,
    LockBidding,
    Users,
    Setting,
    Network
  } from "@/components/action-block/tabs";
  import LiveBidBlock from "@/components/action-block/LiveBidBlock.vue";
  import {
    MailLiveIcon,
    HistoryIcon,
    ConverterCurrencyIcon,
    StatisticIcon,
    BaseIcon,
    LockIcon,
    CogIcon,
    WifiIcon,
    UsersIcon
  } from "@/components/icons";
  import { useAuctionStore } from "@/stores/AuctionStore";
  import { AuctionStatuses } from "@/enum";
  import { isScreenL } from "@/utils/media-size";
  import { AuthPopup } from "@/components/_partials/lot-info-popup";

  const auctionStore = useAuctionStore();
  const authPopupRef = ref<HTMLDivElement>(null);

  const emitter = inject("emitter");
  const defaultViewBox = "0 0 24 24";
  const chatTabKey = "chat";
  const initIsScreenL = isScreenL.value;

  const auctionStatus = computed(() => auctionStore.auction.status);

  const chatObject = {
    key: chatTabKey,
    iconName: "mail-live",
    icon: markRaw(MailLiveIcon),
    iconViewBox: "0 0 22.636 18.109",
    component: markRaw(Chat)
  };
  const statisticObject = {
    key: "statistics",
    iconName: "statistics",
    icon: markRaw(StatisticIcon),
    iconViewBox: "0 0 20.121 20.121",
    component: markRaw(Statistic)
  };
  const lockObject = {
    key: "lock",
    iconName: "lock",
    icon: markRaw(LockIcon),
    iconViewBox: "0 0 17.721 23.259",
    component: markRaw(LockBidding),
    isNeedAuth: true
  };
  const tabs = computed(() => {
    const tabs = [];

    if (auctionStore.user?.is_admin) {
      tabs.push(
        {
          key: "users",
          iconName: "users",
          icon: markRaw(UsersIcon),
          iconViewBox: "0 0 24 16.8",
          component: markRaw(Users)
        },
        chatObject,
        statisticObject,
        {
          key: "setting",
          iconName: "setting",
          icon: markRaw(CogIcon),
          iconViewBox: "0 0 26 25.871",
          component: markRaw(Setting)
        },
        {
          key: "wifi",
          iconName: "wifi",
          icon: markRaw(WifiIcon),
          iconViewBox: "0 0 22 15.552",
          component: markRaw(Network)
        }
      );
    } else {
      if (auctionStatus.value !== AuctionStatuses.ENDED) {
        tabs.push(
          {
            key: "history",
            iconName: "history",
            icon: markRaw(HistoryIcon),
            iconViewBox: "0 0 22.637 22.636",
            component: markRaw(History),
            class: "mob-hidden"
          },
          chatObject,
          {
            key: "converter-currency",
            iconName: "converter-currency",
            icon: markRaw(ConverterCurrencyIcon),
            iconViewBox: "0 0 21.685 21.684",
            component: markRaw(Currency)
          }
        );

        tabs.push(statisticObject);

        if (!initIsScreenL) {
          tabs.push(lockObject);
        }
      } else {
        tabs.push(statisticObject);
      }
    }

    return tabs;
  });

  const activeTab = ref(
    auctionStatus.value === AuctionStatuses.ENDED ? tabs.value[0].key : tabs.value[isScreenL.value ? 1 : 0].key
  );
  watch(
    () => auctionStore.hasNewChatMessage,
    () => {
      const chatTab = getChatTab();
      if (chatTab) {
        addBlinkingToTab(chatTab);
      }
    }
  );

  watch(
    () => auctionStore.pageIsActive,
    (isPageActiveNV) => {
      const chatTab = getChatTab();
      if (chatTab) {
        if (auctionStore.hasNewChatMessage && !isPageActiveNV) {
          addBlinkingToTab(chatTab);
        } else {
          setTimeout(() => removeBlinkingToTab(chatTab), 3000);
        }
      }
    }
  );

  watch(isScreenL, (newValue) => {
    activeTab.value = newValue && activeTab.value === "history" ? tabs.value[1].key : tabs.value[0].key;
    if (!newValue && auctionStore.user && auctionStatus.value !== AuctionStatuses.ENDED) {
      tabs.value.push(lockObject);
    } else {
      let index = tabs.value.findIndex((item) => item.key === "lock");
      if (index !== -1) {
        tabs.value.splice(index, 1);
      }
    }
  });

  const getChatTab = (): HTMLElement | null => {
    return document.getElementById(`${chatTabKey}Ref`);
  };

  emitter?.on("on:user-message", () => {
    activeTab.value = chatTabKey;
  });

  const addBlinkingToTab = (tab: HTMLElement) => {
    tab.classList.add("blinking");
  };

  const removeBlinkingToTab = (tab: HTMLElement) => {
    tab.classList.remove("blinking");
  };
  const changeActiveTab = (tab) => {
    if (tab.isNeedAuth && !auctionStore.user?.id) {
      authPopupRef.value?.open();
    } else {
      activeTab.value = tab.key;
    }
  };
</script>
